/**
 * #825: per-template element order + per-element alignment for the shared
 * property-card templates. Spec: docs/property-card-elements.md.
 *
 * Lives outside widgets/property-card/config.ts on purpose: the dead-setting
 * sentinel excludes config.ts from usage analysis, and it clears a keyed
 * subtree only when the render side reads it through a VARIABLE index —
 * `elements[template]` below is that read.
 */

export const CARD_ELEMENT_IDS = ['title', 'rating', 'location', 'excerpt', 'price', 'meta'] as const
export type CardElementId = (typeof CARD_ELEMENT_IDS)[number]

export const CARD_ELEMENT_ALIGNS = ['default', 'left', 'center', 'right', 'justify'] as const
export type CardElementAlign = (typeof CARD_ELEMENT_ALIGNS)[number]

export interface CardElement {
  id: CardElementId
  align: CardElementAlign
}

/** The templates that render from an element list; the legacy explorer card does not. */
export const CARD_ELEMENT_TEMPLATES = ['classic', 'overlay', 'detailed'] as const
export type CardElementTemplate = (typeof CARD_ELEMENT_TEMPLATES)[number]

export type CardElementsByTemplate = Partial<Record<CardElementTemplate, CardElement[]>>

/**
 * Each template's designed order — today's DOM order, so an untouched widget
 * renders byte-identical markup. Also the element SET a template renders:
 * Classic has no excerpt, Photo Overlay has no rating or excerpt.
 */
export const CARD_ELEMENT_DEFAULT_ORDER: Record<CardElementTemplate, readonly CardElementId[]> = {
  classic: ['title', 'rating', 'location', 'price', 'meta'],
  detailed: ['title', 'rating', 'location', 'excerpt', 'price', 'meta'],
  overlay: ['title', 'price', 'location', 'meta'],
}

export function isCardElementId(value: unknown): value is CardElementId {
  return typeof value === 'string' && (CARD_ELEMENT_IDS as readonly string[]).includes(value)
}

export function isCardElementAlign(value: unknown): value is CardElementAlign {
  return typeof value === 'string' && (CARD_ELEMENT_ALIGNS as readonly string[]).includes(value)
}

export function isCardElementTemplate(value: unknown): value is CardElementTemplate {
  return typeof value === 'string' && (CARD_ELEMENT_TEMPLATES as readonly string[]).includes(value)
}

export function defaultCardElements(template: CardElementTemplate): CardElement[] {
  return CARD_ELEMENT_DEFAULT_ORDER[template].map((id) => ({ id, align: 'default' }))
}

/**
 * Lenient normalization of one stored list. The form autosaves raw, so a
 * half-edited value must degrade instead of throwing at render: unknown and
 * duplicate ids are dropped, ids the template never renders are dropped,
 * missing ids are appended in default order, and an unknown align reads as
 * `default`. A bare id string is accepted for the same reason.
 */
export function normalizeCardElements(template: CardElementTemplate, raw: unknown): CardElement[] {
  const allowed = CARD_ELEMENT_DEFAULT_ORDER[template]
  const out: CardElement[] = []
  const seen = new Set<CardElementId>()
  if (Array.isArray(raw)) {
    for (const item of raw) {
      const record = item && typeof item === 'object' ? (item as Record<string, unknown>) : null
      const id = typeof item === 'string' ? item : record?.id
      if (!isCardElementId(id) || !allowed.includes(id) || seen.has(id)) continue
      const align = record && isCardElementAlign(record.align) ? record.align : 'default'
      seen.add(id)
      out.push({ id, align })
    }
  }
  for (const id of allowed) {
    if (!seen.has(id)) out.push({ id, align: 'default' })
  }
  return out
}

/** True when the list is the template's untouched default (order and alignment). */
export function isDefaultCardElements(template: CardElementTemplate, list: readonly CardElement[]): boolean {
  const defaults = CARD_ELEMENT_DEFAULT_ORDER[template]
  return list.length === defaults.length
    && list.every((el, i) => el.id === defaults[i] && el.align === 'default')
}

/**
 * The list a template renders from: its stored list, normalized, or the
 * designed default when nothing is stored (grid mounts pass no `elements`).
 */
export function resolveCardElements(
  elements: CardElementsByTemplate | undefined,
  template: CardElementTemplate,
): CardElement[] {
  return normalizeCardElements(template, elements?.[template])
}

/** The alignment class for an element's row node; `default` emits nothing. */
export function alignClass(align: CardElementAlign | undefined): string {
  return align && align !== 'default' ? ` hr-pc-al-${align}` : ''
}
